"use client";

import { useEffect, useState } from "react";
import { useConsent } from "./ConsentProvider";
import { detectVisitorCountryCode } from "./detectVisitorCountryCode";

const COOKIE_POLICY_URL = "https://www.pearson.com/en-us/legal-information/cookie-policy.html";
const INDONESIA_COUNTRY_CODE = "ID";

const copy = {
  en: {
    message:
      "We use cookies to understand how Language+ is used so we can improve it. We only turn on analytics if you say yes.",
    policyLabel: "Cookie Policy",
    accept: "Accept",
    reject: "Reject",
    manage: "Cookie settings",
  },
  id: {
    message:
      "Kami menggunakan cookie untuk memahami cara Language+ digunakan agar kami dapat meningkatkannya. Kami hanya mengaktifkan analitik jika Anda menyetujuinya.",
    policyLabel: "Kebijakan Cookie",
    accept: "Terima",
    reject: "Tolak",
    manage: "Pengaturan cookie",
  },
} as const;

function useBannerLocale(): keyof typeof copy {
  const [locale, setLocale] = useState<keyof typeof copy>("en");

  useEffect(() => {
    let cancelled = false;

    void detectVisitorCountryCode().then((countryCode) => {
      if (!cancelled && countryCode === INDONESIA_COUNTRY_CODE) {
        setLocale("id");
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return locale;
}

export function ConsentBanner() {
  const { status, grant, deny, reset } = useConsent();
  const locale = useBannerLocale();
  const text = copy[locale];

  if (status === "unknown") {
    return (
      <div
        role="dialog"
        aria-label={text.policyLabel}
        className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4 sm:px-6"
      >
        <div className="flex w-full max-w-2xl flex-col gap-3 rounded-2xl border border-white/10 bg-[#0D004D]/95 p-5 shadow-2xl backdrop-blur sm:flex-row sm:items-center sm:gap-4">
          <p className="flex-1 text-sm leading-relaxed text-white/85">
            {text.message}{" "}
            <a
              href={COOKIE_POLICY_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="underline underline-offset-2 hover:text-white"
            >
              {text.policyLabel}
            </a>
          </p>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={deny}
              className="min-h-10 rounded-full border border-white/20 px-4 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
            >
              {text.reject}
            </button>
            <button
              type="button"
              onClick={grant}
              className="min-h-10 rounded-full bg-[#C1BFFF] px-4 text-sm font-semibold text-[#0D004D] transition-transform hover:-translate-y-0.5 active:translate-y-0 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              {text.accept}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={reset}
      aria-label={text.manage}
      className="fixed bottom-4 left-4 z-50 rounded-full border border-white/15 bg-[#0D004D]/80 px-3 py-2 text-xs font-medium text-white/70 shadow-lg backdrop-blur transition-colors hover:bg-[#0D004D] hover:text-white"
    >
      {text.manage}
    </button>
  );
}
